import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { Options, UseAxios, makeUseAxios } from "axios-hooks";
import { stringify } from "query-string";
import { z } from "zod";

import config from "config";

import { FetchInstance, useApiRequest } from "./interceptors";
import { ApiError, handleStreamResponse } from "./shared";

interface Api {
  instance: AxiosInstance;
  useAxios: UseAxios;
}

const portalInstance = axios.create({
  withCredentials: true,
  headers: { "Content-Type": "application/json;charset=UTF-8" },
  baseURL: config.API_URL,
  paramsSerializer: (params) => stringify(params),
});

const publicInstance = axios.create({
  withCredentials: true,
  headers: { "Content-Type": "application/json;charset=UTF-8" },
  baseURL: config.PUBLIC_API_URL,
});

const productsInstance = axios.create({
  withCredentials: true,
  headers: { "Content-Type": "application/json;charset=UTF-8" },
  baseURL: config.PRODUCTS_API_URL,
});

const usersInstance = axios.create({
  withCredentials: true,
  headers: { "Content-Type": "application/json;charset=UTF-8" },
  baseURL: config.USER_MANAGEMENT_API_URL,
});

const api = {
  portal: {
    instance: portalInstance,
    useAxios: makeUseAxios({
      axios: portalInstance,
      defaultOptions: { useCache: false },
    }),
  } as Api,
  public: {
    instance: publicInstance,
    useAxios: makeUseAxios({
      axios: publicInstance,
      defaultOptions: { useCache: false },
    }),
  } as Api,
  products: {
    instance: productsInstance,
    useAxios: makeUseAxios({
      axios: productsInstance,
      defaultOptions: { useCache: false },
    }),
  } as Api,
  users: {
    instance: usersInstance,
    useAxios: makeUseAxios({
      axios: usersInstance,
      defaultOptions: { useCache: false },
    }),
  } as Api,
};

const makeUseApiRequest =
  (useAxios: UseAxios, serviceName: string) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  <TResponse, TError extends ApiError, TBody = any>(
    requestConfig: AxiosRequestConfig | string,
    options?: Options
  ) => {
    return useApiRequest<TResponse, TBody, TError>(
      useAxios,
      serviceName,
      requestConfig,
      options
    );
  };

export const usePortalApi = makeUseApiRequest(api.portal.useAxios, "PortalApi");

export const usePublicApi = makeUseApiRequest(api.public.useAxios, "PublicApi");

export const useProductsApi = makeUseApiRequest(
  api.products.useAxios,
  "ProductsApi"
);

export const useUsersApi = makeUseApiRequest(api.users.useAxios, "UsersApi");

export default api;

export class ApiClient {
  axiosInstance: AxiosInstance;
  customFetch: FetchInstance;
  constructor(axiosInstance: AxiosInstance, fetchInstance: FetchInstance) {
    this.axiosInstance = axiosInstance;
    this.customFetch = fetchInstance;
  }

  private validateResponse<ResponseType>(
    response: AxiosResponse,
    schema: z.ZodSchema<ResponseType>
  ): ResponseType {
    const parsedResult = schema.safeParse(response.data);
    if (!parsedResult.success) {
      if (config.ENVIRONMENT === "Integration") {
        // eslint-disable-next-line no-console
        console.error(
          `${response.config.method?.toUpperCase()}:`,
          response.request.responseURL,
          parsedResult.error.message
        );
      }
    }
    return response.data;
  }

  async get<Response>(
    url: string,
    responseSchema: z.ZodSchema<Response>,
    config?: AxiosRequestConfig
  ): Promise<Response> {
    const response = await this.axiosInstance.get(url, config);
    return this.validateResponse(response, responseSchema);
  }

  async post<RequestType = unknown, ResponseType = unknown>(
    {
      url,
      body,
      requestSchema,
      responseSchema,
    }: {
      url: string;
      body?: RequestType;
      requestSchema: z.ZodSchema<RequestType>;
      responseSchema: z.ZodSchema<ResponseType>;
    },
    config?: AxiosRequestConfig
  ): Promise<ResponseType> {
    const validatedRequest = requestSchema.parse(body);
    const response = await this.axiosInstance.post(
      url,
      validatedRequest,
      config
    );
    return this.validateResponse(response, responseSchema);
  }

  async put<RequestType = unknown, ResponseType = unknown>(
    {
      url,
      body,
      requestSchema,
      responseSchema,
    }: {
      url: string;
      body?: RequestType;
      requestSchema: z.ZodSchema<RequestType>;
      responseSchema: z.ZodSchema<ResponseType>;
    },
    config?: AxiosRequestConfig
  ): Promise<ResponseType> {
    const validatedRequest = requestSchema.parse(body);
    const response = await this.axiosInstance.put(
      url,
      validatedRequest,
      config
    );
    return this.validateResponse(response, responseSchema);
  }

  async patch<RequestType = unknown, ResponseType = unknown>(
    {
      url,
      body,
      requestSchema,
      responseSchema,
    }: {
      url: string;
      body: RequestType;
      requestSchema: z.ZodSchema<RequestType>;
      responseSchema: z.ZodSchema<ResponseType>;
    },
    config?: AxiosRequestConfig
  ): Promise<ResponseType> {
    const validatedRequest = requestSchema.parse(body);
    const response = await this.axiosInstance.patch(
      url,
      validatedRequest,
      config
    );
    return this.validateResponse(response, responseSchema);
  }

  async delete<ResponseType>(
    {
      url,
      responseSchema,
    }: {
      url: string;
      responseSchema: z.ZodSchema<ResponseType>;
    },
    config?: AxiosRequestConfig
  ): Promise<ResponseType> {
    const response = await this.axiosInstance.delete(url, config);
    return this.validateResponse(response, responseSchema);
  }

  async streamResponse(
    url: string,
    onNewChunk: (chunk: string) => void,
    options: RequestInit = {}
  ) {
    const response = await this.customFetch(url, options);

    return handleStreamResponse(response, onNewChunk);
  }
}
